import React, { useEffect, useState, useRef, useMemo } from "react";
import "tailwindcss/tailwind.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "@fontsource/source-sans-pro";
import { AddUserForm } from "../../components/forms/AddUserForm";
import { EditUserForm } from "../../components/forms/EditUserForm";
import { AddJournalForm } from "../../components/forms/AddJournalForm";
import { EditJournalForm } from "../../components/forms/EditJournalForm";
import { CustomTable } from "../../components/Table";
import { FaSearch, FaTimes } from "react-icons/fa";
import Pencil from "../../assets/pencil.png";
import {
  fetchUsers,
  fetchUserForForm,
  addUser,
  updateUser,
} from "../../services/apiService/userApi";
import {
  fetchJournals,
  addJournal,
  updateJournal,
} from "../../services/apiService/journalApi";
import useToaster from "../../hooks/useToaster";
import { useSelector } from "react-redux";
import "../../index.css";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/Popover";
import { fieldConfig } from "../../lib/utils";
import { PageSkeletons, Spinner } from "../../components/ui/Skeleton";
import { MdClose, MdOutlineKeyboardArrowRight } from "react-icons/md";

const SettingsComponent = ({ active, setIsReload }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [journalSearch, setJournalSearch] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditForm, setIsEditForm] = useState(false);
  const [activeTab, setActiveTab] = useState("User");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activateUser, setActivateUser] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [users, setUsers] = useState([]);
  const [journals, setJournals] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [journalManagers, setJournalManagers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingJournals, setIsLoadingJournals] = useState(false);
  let [loadingState, setLoadingState] = useState("spinning");
  const TeamOptions = ["All", "Silver", "Blue"];
  const JournalTeams = ["All", "Silver", "Blue"];
  const [allJournalName, setAllJournalName] = useState([]);
  const [activeRowId, setActiveRowId] = useState(null);
  const [popoverOpenId, setPopoverOpenId] = useState(null);
  const scrollContainerRef = useRef(null);
  let [sortConfig, setSortConfig] = useState({
    key: "userName",
    direction: "asc",
  });

  const searchTriggeredRef = useRef(false);
  const sortEffectRanRef = useRef(false);
  const firstTimeRef = useRef(false);
  useEffect(() => {
    const handleScroll = () => {
      setPopoverOpenId(null);
    };

    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  useEffect(() => {
    searchTriggeredRef.current = false;
  }, [activeTab]);

  const showToast = useToaster();
  const { userData, preferences } = useSelector((state) => state.user);

  let FetchUserData = async () => {
    try {
      setIsLoadingUsers(true);
      const response = await fetchUsers({
        key: sortConfig.key,
        direction: sortConfig.direction,
        search: searchTerm.trim(),
        id: userData?.id,
      });
      if (!response) {
        setUsers([]);
        return;
      }
      if (response?.success) {
        let UserResponse = response.data.map((user) => {
          return {
            ...user,
            userName: `${user.firstName} ${user.lastName}`,
          };
        });

        setUsers(UserResponse.filter((user) => user.id !== userData.id));

        if (!firstTimeRef.current) {
          const managers = response.data
            .filter((u) => u.isActive !== false)
            .map((u) => ({
              label: `${u.lastName} ${u.firstName}`,
              value: u.id,
            }))
            .filter((m) => m.label && m.value);

          setJournalManagers(managers);
        } else {
          FetchUserOptions();
        }
      } else {
        setUsers([]);
      }
    } catch (err) {
      if (err.response?.status === 403) {
        return;
      }

      setUsers([]);
    } finally {
      setIsLoadingUsers(false);
      firstTimeRef.current = true;
    }
  };

  let fetchJournalData = async () => {
    try {
      setIsLoadingJournals(true);
      const journalResponse = await fetchJournals({
        key: sortConfig.key,
        direction: sortConfig.direction,
        id: userData.id,
        count: false,
        search: journalSearch.trim(),
        place: "settings",
      });

      const truncate = (str, max = 20) =>
        str.length > max ? str.slice(0, max) + "…" : str;

      if (journalResponse?.success) {
        if (journalResponse?.data?.data?.length > 0) {
          const journals = journalResponse.data.data;

          const enriched = journals.map((journal) => {
            let managersString = "";

            if (
              Array.isArray(journal.managers) &&
              journal.managers.length > 0
            ) {
              const firstManager = journal.managers[0];
              const firstManagerName = truncate(
                `${firstManager.firstName} ${firstManager.lastName}`.trim()
              );

              managersString = firstManagerName;

              if (journal.managers.length > 1) {
                const secondManager = journal.managers[1];
                const secondManagerName = truncate(
                  `${secondManager.firstName} ${secondManager.lastName}`.trim()
                );
                managersString += `, ${secondManagerName}`;
              }

              if (journal.managers.length > 2) {
                const additionalCount = journal.managers.length - 2;
                managersString += ` + ${additionalCount} more`;
              }
            }

            return {
              ...journal,
              managers: managersString,
            };
          });

          setJournals(enriched);
          setAllJournalName(journals.map((j) => j.journalName));
        } else {
          setJournals([]);
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoadingJournals(false);
    }
  };

  let FetchUserOptions = async () => {
    try {
      const response = await fetchUserForForm();
      if (response.success) {
        const { userOptions } = response?.data;
        setJournalManagers(userOptions);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const isInitialLoading = useMemo(() => {
    if (activeTab === "User") {
      return users.length === 0 && isLoadingUsers;
    }
    return journals.length === 0 && isLoadingJournals;
  }, [users, isLoadingUsers, journals, isLoadingJournals, activeTab]);

  const fetchDataOnSearch = () => {
    if (!searchTriggeredRef.current) return;

    if (activeTab === "User") {
      if (searchTerm.trim().length > 2 || !searchTerm.trim().length) {
        FetchUserData();
      }
    } else {
      if (journalSearch.trim().length > 2 || !journalSearch.trim().length) {
        fetchJournalData();
      }
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchDataOnSearch();
    }, 600);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, journalSearch]);

  /**
   * Fetch users and journals from the API and update the state
   * @async
   * @function FetchAll
   */
  useEffect(() => {
    const FetchAll = async () => {
      const startTime = Date.now();
      let success = false;

      if (!hasLoadedOnce) {
        setIsLoading(false);
        setLoadingState("spinning");
      }

      try {
        await FetchUserData();
        // await fetchJournalData();
        success = true;
      } catch (err) {
        showToast("Something went wrong. Please contact admin.");
      } finally {
        const timeTaken = Date.now() - startTime;

        if (!hasLoadedOnce) {
          if (timeTaken < 2000) {
            setLoadingState("data");
          } else if (timeTaken >= 2000 && timeTaken < 5000) {
            setTimeout(() => {
              setLoadingState("data");
            }, 2000);
          } else {
            setTimeout(() => {
              setLoadingState("skeleton");
              setTimeout(() => {
                if (!success) {
                  setIsReload(true);
                } else {
                  setLoadingState("data");
                }
              }, 2000);
            }, 3000);
          }
        }
      }
    };

    FetchAll();
  }, []);

  useEffect(() => {
    if (activeTab === "User") {
      setSearchTerm("");
    } else {
      setJournalSearch("");
    }
    searchTriggeredRef.current = true;
    setTimeout(fetchDataOnSearch, 0);
  }, [activeTab]);

  useEffect(() => {
    if (!sortEffectRanRef.current) {
      sortEffectRanRef.current = true;
      return;
    }
    if (activeTab === "User") {
      FetchUserData();
    } else {
      fetchJournalData();
    }
  }, [sortConfig, preferences, activeTab]);

  /**
   * Checks if the user is an admin and updates the state accordingly
   * @effects
   * @listens - userdata
   */
  useEffect(() => {
    if (
      userData?.articleTaskManagement === "Edit" &&
      userData?.issueTaskManagement === "Edit" &&
      userData?.issueOverview === "Edit" &&
      userData?.articleOverview === "Edit" &&
      userData?.settings === "Edit"
    ) {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
    }
  }, [userData]);

  /**
   * object to hold user data
   */
  const [userObject, setUserData] = useState({
    id: null,
    firstName: "",
    lastName: "",
    email: "",
    team: "",
    articleTaskManagement: "",
    issueTaskManagement: "",
    issueOverview: "",
    articleOverview: "",
    settings: "",
  });

  /**
   * object to hold journal data
   */
  const [journalData, setJournalData] = useState({
    id: null,
    journalName: "",
    journalManager: "",
    url: "",
    team: "",
    coverSubInvitees: 0,
    coverSubmissionDue: 0,
    issueHoldout: 0,
    lastMsForRed: 0,
    issueFinalizing: 0,
    fieldConfiguration: [],
  });

  /**
   * headers for user table
   */
  const header = [
    { key: "userName", value: "NAME" },
    { key: "email", value: "EMAIL" },
    { key: "team", value: "TEAM" },
    {
      key: "articleTaskManagement",
      value: "ARTICLE ASSIGNMENT",
    },
    {
      key: "issueTaskManagement",
      value: "ISSUE TASK ASSIGNMENT",
    },
    { key: "issueOverview", value: "ISSUE OVERVIEW" },
    { key: "articleOverview", value: "JOURNAL OVERVIEW" },
    { key: "settings", value: "SETTINGS" },
  ];

  /**
   * headers for journal table
   */
  const journalHeader = [
    { key: "journalName", value: "JOURNAL" },
    { key: "managers", value: "PRODUCTION EDITOR(S)" },
    { key: "team", value: "TEAM" },
  ];

  /**
   * filters the users
   * @param {string} searchTerm - the search term
   * @returns {Array} - the filtered users
   */

  const filteredUsers = users;

  /**
   * filters the journals
   * @param {string} searchTerm - the search term
   * @returns {Array} - the filtered journals
   */

  const filteredJournals = journals;

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSortConfig({
      key: tab === "User" ? header[0].key : journalHeader[0].key,
      direction: "asc",
    });
  };

  /**
   * Handles user form initialization based on action type
   * @param {Object|null} user - The user object or null for new user
   */
  const handleUserForm = (user) => {
    if (user && user.isActive === false) {
      setUserData(user);
      setActivateUser(true);
      setIsEditForm(false);
    } else if (user && user.isActive === true) {
      setUserData(user);
      setIsEditForm(true);
      setActivateUser(false);
    } else {
      setUserData({
        id: null,
        firstName: "",
        lastName: "",
        email: "",
        team: "",
        articleTaskManagement: "",
        issueTaskManagement: "",
        issueOverview: "",
        articleOverview: "",
        settings: "",
      });
      setIsEditForm(false);
      setActivateUser(false);
    }
    setIsFormOpen(true);
  };

  /**
   * Handles input changes in user form
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
  };

  const capitalize = (str) =>
    typeof str === "string" && str.length > 0
      ? str[0].toUpperCase() + str.slice(1).toLowerCase()
      : str;

  /**
   * Handles form submission for user operations (add/edit/activate)
   * @async
   * @param {React.FormEvent} e - The form submission event
   * @function fetchUsers - the function to fetch updated users from the API
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!navigator.onLine) {
      showToast("You are currently offline. Please check your internet connection!");
      return;
    }

    try {
      const trimmedUser = Object.fromEntries(
        Object.entries(userObject).map(([key, value]) => [
          key,
          typeof value === "string" ? value.trim() : value,
        ])
      );

      if (activateUser) {
        const updatedUser = { ...trimmedUser, isActive: true };
        const response = await updateUser(trimmedUser.id, updatedUser);
        if (response.success) {
          let name = `${trimmedUser.firstName} ${trimmedUser.lastName}`.slice(
            0,
            15
          );
          showToast(`${name} activated successfully`);
        } else {
          showToast(response.message || "Error activating user");
          return;
        }
      } else {
        if (trimmedUser.id) {
          const updatedUser = {
            ...trimmedUser,
          };
          const response = await updateUser(trimmedUser.id, updatedUser);
          if (response.success) {
            let name = `${trimmedUser.firstName} ${trimmedUser.lastName}`.slice(
              0,
              15
            );
            showToast(`${name} Updated Successfully`);
          } else {
            showToast(response.message || "Error updating user");
            return;
          }
        } else {
          const newUser = {
            ...trimmedUser,
          };

          delete newUser.id;
          const response = await addUser(newUser);
          if (response.success) {
            let name = `${trimmedUser.firstName} ${trimmedUser.lastName}`.slice(
              0,
              15
            );
            showToast(`New User ${name} created Successfully`);
          } else {
            showToast(response.message || "Error adding user");
            return;
          }
        }
      }

      await FetchUserData();
      setIsFormOpen(false);
      setActiveRowId(null);
    } catch (error) {
      showToast(error.message || "Something went wrong. Please contact admin.");
    }
  };

  /**
   * Handles journal form initialization based on action type
   * @param {Object|null} journal - The journal object or null for new journal
   */
  const handleJournalForm = (journal) => {
    if (journal) {
      setJournalData({
        ...journal,
        fieldConfiguration: journal.fieldConfiguration || [],
      });
      setIsEditForm(true);
    } else {
      setJournalData({
        id: null,
        journalName: "",
        journalManager: "",
        url: "",
        team: "",
        coverSubInvitees: 0,
        coverSubmissionDue: 0,
        issueHoldout: 0,
        lastMsForRed: 0,
        issueFinalizing: 0,
        fieldConfiguration: fieldConfig,
      });
      setIsEditForm(false);
    }
    setIsFormOpen(true);
  };

  /**
   * Handles input changes in journal form
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event
   */
  const handleJournalInputChange = (e) => {
    const { name, value } = e.target;
    setJournalData((prev) => ({
      ...prev,
      [name]: Array.isArray(value) ? value : value,
    }));
  };

  /**
   * Handles form submission for journal operations (add/edit)
   * @async
   * @param {React.FormEvent} e - The form submission event
   * @param {Object} updatedJournalData - The updated journal data
   * @function fetchJournals - the function to fetch updated journals from the API
   */
  const handleJournalSubmit = async (e, updatedJournalData) => {
    e.preventDefault();

    if (!navigator.onLine) {
      showToast("You are currently offline. Please check your internet connection!");
      return;
    }

    try {
      if (!updatedJournalData) {
        showToast("No journal data provided to save");
        return;
      }

      const trimmedJournal = Object.fromEntries(
        Object.entries(updatedJournalData).map(([key, value]) => [
          key,
          typeof value === "string" ? value.trim() : value,
        ])
      );

      let response;
      if (trimmedJournal.id) {
        response = await updateJournal(trimmedJournal.id, trimmedJournal);
        if (response.success) {
          let FullName = `${trimmedJournal.journalName}`.slice(0, 15);
          showToast(`${FullName} updated successfully`);
        } else {
          showToast(response.message || "Failed to update journal");
          return;
        }
      } else {
        const newJournal = { ...trimmedJournal };
        delete newJournal.id;
        response = await addJournal(newJournal);
        if (response.success) {
          let FullName = `${newJournal.journalName}`.slice(0, 15);
          showToast(`New journal ${FullName} created successfully`);
        } else {
          showToast(response.message || "Failed to create journal");
          return;
        }
      }

      setIsFormOpen(false);
      await fetchJournalData();
      setActiveRowId(null);
    } catch (error) {
      showToast(error.message || "Something went wrong. Please contact admin.");
    }
  };

  /**
   * Renders action buttons for user table rows
   * @param {Object} row - The user row data
   * @returns {React.ReactElement} Action buttons JSX
   */
  const userActions = (row) => {
    if (!row.isActive) {
      return (
        <button
          className={`p-2 font-semibold text-[13px] w-[90px] h-[32px] flex items-center justify-center whitespace-nowrap ${
            isAdmin
              ? "text-[#1C40C9] cursor-pointer hover:bg-[#B8C7FE]"
              : "text-gray-400 opacity-50"
          } m-1 rounded-lg`}
          onClick={() => isAdmin && handleUserForm(row)}
        >
          Activate User
        </button>
      );
    } else {
      const isEditable = userData?.settings?.toLowerCase() === "edit";
      return (
        <div className="flex items-center">
          <button
            disabled={!isEditable}
            onClick={isEditable ? () => handleUserForm(row) : null}
            className={`p-2 rounded-md mr-2 ${
              isEditable
                ? "text-[#35424D] hover:bg-[#B8C7FE] cursor-pointer"
                : "text-gray-400 opacity-50 cursor-not-allowed"
            }`}
          >
            <img
              src={Pencil}
              alt="Edit"
              className="max-w-4 min-w-2 max-h-4 min-h-2"
            />
          </button>
          <Popover
            open={popoverOpenId === row.id && isAdmin}
            onOpenChange={(open) => setPopoverOpenId(open ? row.id : null)}
          >
            <PopoverTrigger asChild>
              <button
                disabled={!isEditable}
                className={`px-4 py-2 rounded-md ${
                  isEditable && isAdmin
                    ? "text-[#35424D] hover:bg-[#B8C7FE] cursor-pointer"
                    : "text-gray-400 opacity-50 cursor-not-allowed"
                }`}
                aria-label="User actions"
              >
                <i className="fas fa-ellipsis-v h-3 w-1"></i>
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="bg-white p-0 w-[140px] h-[46px] shadow-lg rounded-md border border-gray-200"
              align="end"
              sideOffset={5}
            >
              <button
                className="px-4 py-2 mt-1 text-sm text-[#35424D] hover:bg-[#EDF0FC] w-full text-left"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeactivateClick(row);
                }}
              >
                Deactivate User
              </button>
            </PopoverContent>
          </Popover>
        </div>
      );
    }
  };

  /**
   * Renders action buttons for journal table rows
   * @param {Object} row - The journal row data
   * @returns {React.ReactElement} Action buttons JSX
   */
  const journalActions = (row) => {
    // checking for admin status
    const isEditable =
      userData?.settings?.toLowerCase() === "edit" &&
      userData?.issueOverview?.toLowerCase() === "edit" &&
      userData?.articleOverview?.toLowerCase() === "edit" &&
      userData?.issueTaskManagement?.toLowerCase() === "edit" &&
      userData?.articleTaskManagement?.toLowerCase() === "edit";

    return (
      <>
        <img
          src={Pencil}
          alt="Edit"
          className={` w-8 h-8 px-2 py-2 rounded-md text-right ${
            isEditable
              ? "hover:bg-[#B8C7FE] cursor-pointer"
              : "opacity-50 cursor-not-allowed"
          }`}
          onClick={isEditable ? () => handleJournalForm(row) : null}
        />
      </>
    );
  };

  /**
   * Handles search input changes
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event
   */
  const handleChange = (e) => {
    searchTriggeredRef.current = true;
    if (activeTab === "User") {
      setSearchTerm(e.target.value);
    } else {
      setJournalSearch(e.target.value);
    }
  };

  /**
   * Clears the search term and resets focus state
   */
  const handleClear = () => {
    if (activeTab === "User") {
      setIsLoadingUsers(true);
      setSearchTerm("");
    } else {
      setIsLoadingJournals(true);
      setJournalSearch("");
    }
    setIsFocused(false);
  };

  /**
   * setting the selected user to deactivate
   * @param {object} row - to deactivate
   */
  const handleDeactivateClick = (row) => {
    setSelectedUser(row);
    setIsModalOpen(true);
    setPopoverOpenId(false);
  };

  /**
   * Deactivates the user in api
   * @async
   */
  const DeactivateUser = async () => {
    if (selectedUser) {
      try {
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === selectedUser.id ? { ...user, isActive: false } : user
          )
        );
        await updateUser(selectedUser.id, { ...selectedUser, isActive: false });
        setTimeout(async () => {
          await FetchUserData();
        }, 3000);
      } catch (error) {
        console.error("Error updating user:", error);
      }
    }
    setIsModalOpen(false);
  };

  /**
   * Cancels the current form operation
   */
  const handleCancel = () => {
    setIsFormOpen(false);
    setActiveRowId(null);
  };

  return loadingState === "spinning" ? (
    <Spinner />
  ) : loadingState === "skeleton" ? (
    <PageSkeletons />
  ) : (
    <div
      className="bg-gray-100 flex flex-col"
      style={{
        fontFamily: "Source Sans Pro",
        height: "calc(100vh - 80px)",
      }}
    >
      {/* Main Content */}
      <div className="flex flex-1 flex-col h-full overflow-hidden">
        <div className="flex-1 pt-6 px-6 bg-white flex flex-col h-full">
          {/* Conditional Rendering for PageSkeleton */}

          <>
            {/* Your normal content goes here */}
            {!isFormOpen ? (
              <>
                {/* Fixed Header Section */}
                <div className="header-section shrink-0 bg-white sticky top-0 z-10">
                  {/* Bread */}
                  <div className="flex items-center justify-start space-x-1 mb-3 ">
                    <p className="text-[#868E94] font-medium text-[13px]">
                      {active}
                    </p>{" "}
                    <MdOutlineKeyboardArrowRight className="text-[#5D6871] text-[16px]" />{" "}
                    <p className="text-[#5D6871] font-medium text-[13px]">
                      {activeTab}
                    </p>
                  </div>
                  {/* Title */}
                  <h1 className="text-[#20282E] text-[23px] h-[32px] font-semibold mb-2">
                    {active}
                  </h1>
                  {/* Tabs */}
                  <div className="flex items-center justify-between border-b-2 border-gray-200 mb-3">
                    {/* Tab Buttons */}
                    <div className="flex">
                      <button
                        className={`p-3 flex items-center h-[28px] ${
                          activeTab === "User"
                            ? "border-b-2 border-[#1C40C9] text-[13px] text-[#35424D] font-semibold "
                            : "font-medium hover:text-[#35424D] text-[13px]"
                        }`}
                        onClick={() => handleTabChange("User")}
                      >
                        User
                      </button>
                      <button
                        className={`p-3 flex items-center h-[28px] ${
                          activeTab === "Journal"
                            ? "border-b-2 border-[#1C40C9] text-[13px] text-[#35424D] font-semibold "
                            : "font-medium hover:text-[#35424D] text-[13px]"
                        }`}
                        onClick={() => handleTabChange("Journal")}
                      >
                        Journal
                      </button>
                    </div>
                    {/* Add user or journal Button */}
                    {isAdmin && (
                      <button
                        className="text-[#1C40C9] p-2 mt-[-5px] rounded-lg text-[13px] hover:bg-[#E1E6FF] flex items-center space-x-1"
                        onClick={() =>
                          activeTab === "User"
                            ? handleUserForm()
                            : handleJournalForm()
                        }
                      >
                        <i className="fas fa-plus"></i>
                        <span>
                          Add {activeTab === "User" ? "User" : "Journal"}
                        </span>
                      </button>
                    )}
                  </div>
                  {/* Search Bar */}
                  <div className="relative w-[246px] mb-4">
                    <input
                      id="search-input"
                      type="text"
                      className={`border rounded-[4px] w-[246px] h-[32px] py-[4px] px-[12px] pr-[32px] text-sm transition-colors ${
                        isFocused
                          ? " focus:border-[#35424D] focus:border focus:outline-none"
                          : "border-[#C3C3C3]"
                      }`}
                      placeholder="Search"
                      maxLength={100}
                      value={activeTab === "User" ? searchTerm : journalSearch}
                      onChange={handleChange}
                      onFocus={() => setIsFocused(true)}
                      onBlur={() => {
                        if (searchTerm === "") {
                          setIsFocused(false);
                        }
                      }}
                    />
                    {((activeTab === "User" && searchTerm.length === 0) ||
                      (activeTab === "Journal" &&
                        journalSearch.length === 0)) && (
                      <FaSearch className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#C3C3C3]" />
                    )}
                    {((activeTab === "User" && searchTerm.length > 0) ||
                      (activeTab === "Journal" &&
                        journalSearch.length > 0)) && (
                      <FaTimes
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#C3C3C3]"
                        onClick={handleClear}
                      />
                    )}
                  </div>

                  {/* Search Results */}
                  {searchTerm && activeTab === "User" && (
                    <p className="mt-1 mb-4 text-sm text-gray-600">
                      Showing search results:{" "}
                      <span className="text-black italic">'{searchTerm}'</span>
                    </p>
                  )}
                  {journalSearch && activeTab === "Journal" && (
                    <p className="mt-1 mb-4 text-sm text-gray-600">
                      Showing search results:{" "}
                      <span className="text-black italic">
                        '{journalSearch}'
                      </span>
                    </p>
                  )}
                </div>

                {/* Table Section */}
                <div
                  ref={scrollContainerRef}
                  className={` overflow-y-auto ${
                    activeTab === "User" && filteredUsers.length > 0
                      ? "border-[#DCDCDC] rounded-md border"
                      : activeTab === "Journal" && filteredJournals.length > 0
                      ? "border-[#DCDCDC] rounded-md border"
                      : ""
                  } overflow-x-visible relative`}
                >
                  {isInitialLoading ||
                  (isLoadingUsers && filteredUsers.length === 0) ||
                  (isLoadingJournals && filteredJournals.length === 0) ? (
                    <div className="h-[50vh]">
                      <Spinner />
                    </div>
                  ) : (
                    <div className={`min-w-full relative`}>
                      {activeTab === "User" ? (
                        <CustomTable
                          data={filteredUsers}
                          actions={userActions}
                          searchTerm={searchTerm.trim()}
                          setSearchTerm={setSearchTerm}
                          setLoading={setIsLoadingUsers}
                          headers={header}
                          activeTab={activeTab}
                          setActiveRowId={setActiveRowId}
                          activeRowId={activeRowId}
                          sortConfig={sortConfig}
                          setSortConfig={setSortConfig}
                        />
                      ) : (
                        <CustomTable
                          data={filteredJournals}
                          actions={journalActions}
                          searchTerm={journalSearch.trim()}
                          setSearchTerm={setJournalSearch}
                          setLoading={setIsLoadingJournals}
                          headers={journalHeader}
                          activeTab={activeTab}
                          setActiveRowId={setActiveRowId}
                          activeRowId={activeRowId}
                          sortConfig={sortConfig}
                          setSortConfig={setSortConfig}
                        />
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : isEditForm ? (
              activeTab === "User" ? (
                <EditUserForm
                  userdata={userObject}
                  oncancel={handleCancel}
                  onSave={handleSubmit}
                  onchange={handleInputChange}
                  formTitle="Edit User"
                  active1={active}
                  active2={activeTab}
                  teams={TeamOptions}
                />
              ) : (
                <EditJournalForm
                  initialJournalData={journalData}
                  oncancel={handleCancel}
                  onSave={handleJournalSubmit}
                  onchange={handleJournalInputChange}
                  active1={active}
                  active2={activeTab}
                  teams={JournalTeams}
                  journalManagers={journalManagers}
                  journalNames={allJournalName}
                />
              )
            ) : activeTab === "User" ? (
              <AddUserForm
                userdata={userObject}
                oncancel={handleCancel}
                onSave={handleSubmit}
                onchange={handleInputChange}
                formTitle={activateUser ? "User Activation" : "Add User"}
                teams={TeamOptions}
              />
            ) : (
              <AddJournalForm
                journalData={journalData}
                oncancel={handleCancel}
                onSave={handleJournalSubmit}
                onchange={handleJournalInputChange}
                journalManagers={journalManagers}
                teams={JournalTeams}
                journalNames={allJournalName}
              />
            )}
          </>
        </div>
      </div>

      {/* Deactivate User Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg h-[265px] w-[376px]">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-[22px] h-[24px] font-semibold">
                De-activate User
              </h2>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setActiveRowId(null);
                }}
              >
                <MdClose className="text-[#35424D] w-[16px] h-[16px]" />
              </button>
            </div>
            <hr className="mb-4" />
            <p className="mb-20 text-[16px] h-[24px] text-[#35424D]">
              Are you sure you want to de-activate this User?
            </p>
            <hr className="mb-4" />
            <div className="flex justify-end space-x-4">
              <button
                className="text-blue-600 hover:underline"
                onClick={() => {
                  setIsModalOpen(false);
                  setActiveRowId(null);
                }}
              >
                Cancel
              </button>
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                onClick={DeactivateUser}
              >
                De-activate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsComponent;
